import type { AxiosError } from 'axios'
import type { ApiErrorType } from './ApiError'
import {
  ApiError as ApiErrorClass,
  NetworkError,
  ValidationError,
  UnauthorizedError,
  ForbiddenError,
  NotFoundError,
  RateLimitError,
  ServerError,
  AbortError,
} from './ApiError'

/** Сериализуемый объект ошибки для хранения в store (SSR-safe, не ломает devalue) */
export interface SerializableError {
  message: string
  statusCode?: number
}

const SERVER_NOT_RESPONDING_MESSAGE = 'Server is not responding'

export function isServerNotRespondingError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const message = (error as Record<string, unknown>).message
  return message === SERVER_NOT_RESPONDING_MESSAGE
}

/**
 * Обработчик ошибок API
 * Преобразует HTTP ошибки в типизированные исключения
 */
export class ErrorHandler {
  /**
   * Обрабатывает ошибку и преобразует в соответствующий тип
   */
  static handle(error: unknown): ApiErrorType {
    // Если уже наша ошибка - возвращаем как есть
    if (error instanceof ApiErrorClass) {
      return error
    }

    // Обработка AbortError
    if (error instanceof Error && (error.name === 'AbortError' || error.name === 'CanceledError')) {
      return new AbortError('Request was aborted')
    }

    // Обработка AxiosError
    if (this.isAxiosError(error)) {
      const axiosError = error as AxiosError
      
      // Обработка отмененного запроса
      if (axiosError.code === 'ERR_CANCELED' || axiosError.message.includes('canceled')) {
        return new AbortError('Request was aborted')
      }

      // Обработка HTTP ошибок с response
      if (axiosError.response) {
        const statusCode = axiosError.response.status
        const message = axiosError.response.statusText || axiosError.message || 'API request failed'
        const responseData = axiosError.response.data

        switch (statusCode) {
          case 400:
            return new ValidationError(
              message,
              this.extractValidationErrors(responseData),
              responseData
            )
          case 401:
            return new UnauthorizedError(message, responseData)
          case 403:
            return new ForbiddenError(message, responseData)
          case 404:
            return new NotFoundError(message, responseData)
          case 429:
            return new RateLimitError(
              this.extractMessage(responseData) || message,
              responseData
            )
          default:
            if (statusCode >= 500) {
              return new ServerError(message, statusCode, responseData)
            }
            return new ApiErrorClass(message, statusCode, responseData)
        }
      }

      // Обработка сетевых ошибок (без response) — сервер недоступен
      if (axiosError.request) {
        return new NetworkError(SERVER_NOT_RESPONDING_MESSAGE, axiosError)
      }

      return new NetworkError(SERVER_NOT_RESPONDING_MESSAGE, axiosError)
    }

    // Обработка FetchError (для обратной совместимости)
    if (error && typeof error === 'object' && 'statusCode' in error) {
      const fetchError = error as { statusCode: number; statusMessage?: string; data?: unknown }
      const statusCode = fetchError.statusCode
      const message = fetchError.statusMessage || 'API request failed'
      const response = fetchError.data

      switch (statusCode) {
        case 400:
          return new ValidationError(
            message,
            this.extractValidationErrors(response),
            response
          )
        case 401:
          return new UnauthorizedError(message, response)
        case 403:
          return new ForbiddenError(message, response)
        case 404:
          return new NotFoundError(message, response)
        case 429:
          return new RateLimitError(
            this.extractMessage(response) || message,
            response
          )
        default:
          if (statusCode >= 500) {
            return new ServerError(message, statusCode, response)
          }
          return new ApiErrorClass(message, statusCode, response)
      }
    }

    // Обработка сетевых ошибок (сервер не отвечает)
    if (error instanceof Error) {
      if (
        error.message.includes('network') ||
        error.message.includes('fetch') ||
        error.message.includes('ECONNREFUSED') ||
        error.message.includes('ENOTFOUND') ||
        error.message.includes('timeout')
      ) {
        return new NetworkError(SERVER_NOT_RESPONDING_MESSAGE, error)
      }
      return new ApiErrorClass(error.message, undefined, undefined, error)
    }

    // Неизвестная ошибка
    return new ApiErrorClass(
      typeof error === 'string' ? error : 'Unknown error occurred',
      undefined,
      error
    )
  }

  /**
   * Проверяет, является ли ошибка AxiosError
   */
  private static isAxiosError(error: unknown): boolean {
    return (
      error !== null &&
      typeof error === 'object' &&
      'isAxiosError' in error &&
      (error as any).isAxiosError === true
    )
  }

  /**
   * Извлекает текстовое сообщение из ответа API (формат ApiErrorResponse: success: false, message).
   */
  private static extractMessage(response: unknown): string | undefined {
    if (!response || typeof response !== 'object') return undefined
    const obj = response as Record<string, unknown>
    if (typeof obj.message === 'string') return obj.message
    if (typeof obj.error === 'string') return obj.error
    if (obj.data && typeof obj.data === 'object' && typeof (obj.data as Record<string, unknown>).message === 'string') {
      return (obj.data as Record<string, unknown>).message as string
    }
    return undefined
  }

  /**
   * Извлекает ошибки валидации из ответа
   */
  private static extractValidationErrors(
    response: unknown
  ): Record<string, string[]> | undefined {
    if (!response || typeof response !== 'object') {
      return undefined
    }

    // Попытка извлечь errors из различных форматов ответа
    const responseObj = response as Record<string, unknown>

    if ('errors' in responseObj && typeof responseObj.errors === 'object') {
      return responseObj.errors as Record<string, string[]>
    }

    if ('validation' in responseObj && typeof responseObj.validation === 'object') {
      return responseObj.validation as Record<string, string[]>
    }

    return undefined
  }

  /** Используется в API client для проверки отмены запроса. */
  static isAbortError(error: unknown): error is AbortError {
    return error instanceof AbortError
  }

  /**
   * Преобразует ошибку в сериализуемый объект для store (SSR-safe).
   * Избегает "Cannot stringify arbitrary non-POJOs" при гидрации Nuxt.
   */
  static toSerializable(error: Error | ApiErrorType): SerializableError {
    const withCode = error as ApiErrorClass
    return {
      message: error.message,
      statusCode: withCode.statusCode,
    }
  }
}

